import { useState, useCallback, useMemo } from 'react';

/**
 * Hook for managing structure selection state and logic
 * @param {Array} prepFiles - Array of prep files
 * @returns {Object} Selection state and handlers
 */
export function useStructureSelection(prepFiles = []) {
  const [selectedPrepIds, setSelectedPrepIds] = useState(new Set());
  
  // Get selected files based on selection IDs
  const selectedFiles = useMemo(() => 
    prepFiles.filter(row => selectedPrepIds.has(row.id)),
    [prepFiles, selectedPrepIds]
  );
  
  // Handle row selection changes from DataGrid Pro
  const applyRowSelection = useCallback((newRowSelectionModel, displayData = prepFiles) => {
    // DataGrid Pro uses object format: { ids: Set, type: 'include' | 'exclude' }
    let newSelection;
    
    if (newRowSelectionModel && typeof newRowSelectionModel === 'object' && newRowSelectionModel.ids instanceof Set) {
      if (newRowSelectionModel.type === 'exclude') {
        // 'exclude' with empty set means select all
        // 'exclude' with some IDs means select all except those IDs
        if (newRowSelectionModel.ids.size === 0) {
          // Select all rows
          newSelection = new Set(displayData.map(row => row.id));
        } else {
          // Select all except the excluded IDs
          const excludedIds = newRowSelectionModel.ids;
          newSelection = new Set(displayData.filter(row => !excludedIds.has(row.id)).map(row => row.id));
        }
      } else {
        // 'include' means select only the specified IDs
        newSelection = newRowSelectionModel.ids;
      }
    } else if (Array.isArray(newRowSelectionModel)) {
      // Fallback for basic DataGrid format (shouldn't happen with DataGrid Pro)
      newSelection = new Set(newRowSelectionModel);
    } else {
      // Default to empty selection
      newSelection = new Set();
    }
    
    setSelectedPrepIds(newSelection);
    return newSelection;
  }, [prepFiles]);
  
  // Clear selection
  const clearSelection = useCallback(() => {
    setSelectedPrepIds(new Set());
  }, []);
  
  // Select all
  const selectAll = useCallback(() => {
    setSelectedPrepIds(new Set(prepFiles.map(row => row.id)));
  }, [prepFiles]);
  
  return {
    selectedPrepIds,
    setSelectedPrepIds,
    selectedFiles,
    selectedCount: selectedPrepIds.size,
    applyRowSelection,
    clearSelection,
    selectAll
  };
}